import React, { useState } from 'react';
import { connect } from 'react-redux';
import { getEntityById } from '../../utils/DataAccessUtils';
import {
	getWorkOrderStarState,
	isWorkOrderRemindersEnabled,
	parseRemindAt,
} from '../../data/workOrderStarLabels';
import useWorkOrderStarSocket, {
	STAR_CHANGE_ACTION,
	StarChangeAction,
	WorkOrderStarChangeMessage,
} from '../../hooks/useWorkOrderStarSocket';
import useMountEffect from '../../hooks/useMountEffect';
import { markReminderRinging, ringOverdueReminders } from '../../hooks/useReminderRinging';
import { isLastActiveTab, trackActiveTab } from '../../utils/ActiveTabUtils';
import {
	applyWorkOrderStarChange,
	refreshActiveReminderCount,
	setActiveReminderCount,
} from '../../thunks/work_order_user_stars_thunks';
import {
	markUserNotificationsReadForBuyer,
	markUserNotificationsReadForSupplier,
	userNotificationsRestCrudThunksForBuyer,
	userNotificationsRestCrudThunksForSupplier,
} from '../../thunks/user_notifications_thunks';
import {
	REMINDER_NOTIFICATIONS_COUNT,
	UNREAD_NOTIFICATION_TYPES,
	UNREAD_REMINDER_NOTIFICATIONS,
	WORK_ORDER_REMINDER_NOTIFICATION_TYPE,
} from '../../utils/NotificationUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import ReminderAlertCard from './ReminderAlertCard';

interface ReminderAlert {
	reminder: WorkOrderStarChangeMessage;
	// The cached work order's star when the reminder arrived (see starKey).
	starKeyOnArrival: string | null;
}

interface WorkOrdersSlice {
	records?: Record<string, any>;
	searchRecords?: Record<string, any>;
}

interface Props {
	workOrders: WorkOrdersSlice;
	refreshDueCount: () => void;
	setDueCount: (count: number) => void;
	applyStarChange: (message: WorkOrderStarChangeMessage) => void;
	// Marks a work order's reminder notification read, then refreshes the Inbox counts.
	markReminderRead: (workOrderId: number) => Promise<unknown>;
}

// The cached star's reminder time as a key ('none' if untracked); null when the WO isn't cached.
const starKey = (workOrderId: number, workOrders: WorkOrdersSlice): string | null => {
	const workOrder = getEntityById(workOrderId, workOrders, null);
	if (!workOrder) return null;
	const { starred, remindAt } = getWorkOrderStarState(workOrder);
	return starred && remindAt ? String(remindAt.valueOf()) : 'none';
};

// For a message without remindCondition; the message is preferred, as search records lack it.
const cachedCondition = (workOrderId: number, workOrders: WorkOrdersSlice): string | null => {
	const workOrder = getEntityById(workOrderId, workOrders, null);
	return workOrder ? getWorkOrderStarState(workOrder).condition : null;
};

// Actions that end a due reminder: its card comes down.
const CARD_CLEARING_ACTIONS: StarChangeAction[] = [
	STAR_CHANGE_ACTION.REMINDER_SKIPPED,
	STAR_CHANGE_ACTION.REMINDER_STOPPED,
	STAR_CHANGE_ACTION.REMINDER_SNOOZED,
	STAR_CHANGE_ACTION.UNSTARRED,
];

/**
 * Mounted at the app root, this follows the user's star changes over the WebSocket
 * (`wo_star_change`): it keeps cached work orders' stars current and, with reminders on, the due
 * count, and shows a top-right card for each reminder that comes due. Every open tab hears the
 * message, but only the tab the user was last in shows the card. Cards live for this page load: a
 * card leaves when snoozed, stopped or closed, and nothing is refetched after a reload — the Inbox
 * keeps the reminder.
 */
const ReminderAlertStack: React.FC<Props> = ({
	workOrders,
	refreshDueCount,
	setDueCount,
	applyStarChange,
	markReminderRead,
}) => {
	const [alerts, setAlerts] = useState<ReminderAlert[]>([]);

	useMountEffect(() => trackActiveTab());

	const removeAlert = (workOrderId: number) =>
		setAlerts((current) => current.filter((alert) => alert.reminder.workOrderId !== workOrderId));

	useWorkOrderStarSocket((message) => {
		const remindersEnabled = isWorkOrderRemindersEnabled();
		// Reconnected: what came due while it was down rings, without a card, and the due count is
		// re-read since messages may have been missed.
		if (!message) {
			if (!remindersEnabled) return;
			ringOverdueReminders();
			refreshDueCount();
			return;
		}
		// The row, detail page and Tracked groups follow the star as it now stands.
		applyStarChange(message);
		if (!remindersEnabled) return;
		// The due count (side menu, Tracked tab) comes with every message.
		if (typeof message.activeReminderCount === 'number') setDueCount(message.activeReminderCount);
		if (CARD_CLEARING_ACTIONS.includes(message.action)) {
			removeAlert(message.workOrderId);
			return;
		}
		if (message.action !== STAR_CHANGE_ACTION.REMINDER_DUE) return;
		// The backend fired it (its condition held): every bell on this WO starts ringing, in every tab.
		markReminderRinging(message.workOrderId, parseRemindAt(message.remindAt));
		if (!isLastActiveTab()) return;
		setAlerts((current) => [
			{ reminder: message, starKeyOnArrival: starKey(message.workOrderId, workOrders) },
			// A reminder that rings again (e.g. snoozed elsewhere) replaces its old card.
			...current.filter((alert) => alert.reminder.workOrderId !== message.workOrderId),
		]);
	});
	// The card leaves at once; a failed read only leaves the notification unread in the Inbox.
	const readAlert = (workOrderId: number) => {
		removeAlert(workOrderId);
		markReminderRead(workOrderId).catch(() => undefined);
	};

	// Hide a card once its WO's cached star changes (snoozed/stopped elsewhere) — but not to this
	// reminder's time, since the cache can lag the message.
	const visibleAlerts = alerts.filter((alert) => {
		const currentKey = starKey(alert.reminder.workOrderId, workOrders);
		const remindAt = parseRemindAt(alert.reminder.remindAt);
		return (
			currentKey === alert.starKeyOnArrival ||
			(!!remindAt && currentKey === String(remindAt.valueOf()))
		);
	});

	if (visibleAlerts.length === 0) return null;

	// Padded so the scroll box doesn't clip card shadows; cards still sit 16px right, 80px top.
	return (
		<div className="pointer-events-none fixed right-0 top-[64px] z-[999] flex max-h-[calc(100vh-64px)] flex-col gap-xs overflow-y-auto pb-[40px] pl-[40px] pr-m pt-m">
			{visibleAlerts.map((alert) => (
				<ReminderAlertCard
					key={alert.reminder.workOrderId}
					reminder={alert.reminder}
					condition={
						alert.reminder.remindCondition !== undefined
							? alert.reminder.remindCondition || null
							: cachedCondition(alert.reminder.workOrderId, workOrders)
					}
					onDone={() => removeAlert(alert.reminder.workOrderId)}
					onRead={() => readAlert(alert.reminder.workOrderId)}
				/>
			))}
		</div>
	);
};

const mapStateToProps = (state) => ({
	workOrders: state.work_orders,
	userType: state.session.userType,
});

const mapDispatchToProps = (dispatch) => ({
	refreshDueCountAs: (userType: string) => dispatch(refreshActiveReminderCount(userType)),
	setDueCount: (count: number) => dispatch(setActiveReminderCount(count)),
	applyStarChange: (message: WorkOrderStarChangeMessage) =>
		dispatch(applyWorkOrderStarChange(message)),
	markReminderReadAs: (userType: string, workOrderId: number) => {
		const isSupplier = userType === ROLE_TYPES.SUPPLIER;
		const markRead = isSupplier
			? markUserNotificationsReadForSupplier
			: markUserNotificationsReadForBuyer;
		const notificationThunks = isSupplier
			? userNotificationsRestCrudThunksForSupplier
			: userNotificationsRestCrudThunksForBuyer;
		return dispatch(markRead(WORK_ORDER_REMINDER_NOTIFICATION_TYPE, workOrderId)).then(() =>
			Promise.all([
				dispatch(notificationThunks.countBy(UNREAD_NOTIFICATION_TYPES, 'TASKS_NOTIFICATIONS')),
				dispatch(
					notificationThunks.countBy(UNREAD_REMINDER_NOTIFICATIONS, REMINDER_NOTIFICATIONS_COUNT)
				),
			])
		);
	},
});

const mergeProps = (stateProps, dispatchProps) => ({
	workOrders: stateProps.workOrders,
	refreshDueCount: () => dispatchProps.refreshDueCountAs(stateProps.userType),
	setDueCount: dispatchProps.setDueCount,
	applyStarChange: dispatchProps.applyStarChange,
	markReminderRead: (workOrderId: number) =>
		dispatchProps.markReminderReadAs(stateProps.userType, workOrderId),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(ReminderAlertStack as any) as React.ComponentType<{}>;
